import * as React from "react";
import {
  Input,
  Col,
  Row,
  Select,
  Radio,
  Divider,
  Button,
  Card,
  Upload,
  Tooltip,
  Tree,
  Badge,
  FormInstance,
  Form,
} from "antd";
import { Modal } from "Components/General/Modal/GenericModal";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { projectGroupList } from "Pages/Admin/Stores/ProjectGroupStore";
import { ProjectTabProps } from "../Projects";
import { notificationError, notificationSuccess } from "utils/libHelpers/AntdNotification";
import { UploadChangeParam, UploadFile, UploadProps } from "antd/lib/upload/interface";
import { API_URL } from "General/APIEndpoints";
import { API } from "General/API";
import { stringSort } from "utils/defaultSort";
import { userInfoStore } from "Stores/UserInfoStore";
import { BadgeProps } from "antd/lib/badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface Preset {
  value: API_ADMINPROJECT_PRESET;
  label: string;
}

interface BasicTabState {
  uploading: StoreState;
  fileList: UploadFile[];
  isModalVisible: boolean;
  presets: Preset[];
  treeStructure: API_TreeElement | Array<API_TreeElement>;
}

interface BasicTabProps extends ProjectTabProps, WithTranslation {
  setFolderStructure: (folderStructure: API_TreeElement | Array<API_TreeElement>) => void;
  data: API_AdminProject;
  fetchPresets: () => Promise<{ [key in API_ADMINPROJECT_PRESET]: string }>;
  formRef: React.RefObject<FormInstance>;
}

export const BasicTab = withTranslation(I18N_NS.ADMIN)(
  observer(
    class extends React.Component<BasicTabProps, BasicTabState> {
      constructor(props: BasicTabProps) {
        super(props);
        this.state = {
          uploading: "RAW",
          isModalVisible: false,
          fileList: [],
          presets: [],
          treeStructure: [],
        };
      }

      componentDidMount() {
        (async () => {
          if (!projectGroupList.data.length) {
            await projectGroupList.fetch();
          }
          if (!this.props.isEditView) {
            const response = await this.props.fetchPresets();
            const keys = Object.keys(response);
            if (response) {
              this.setState({
                presets: keys.map((item) => ({ value: item as API_ADMINPROJECT_PRESET, label: response[item] })),
              });
            }
          }
        })();
      }

      get securityLevels() {
        return [
          {
            id: "NONE",
            name: "Normal",
            description: "Project is protected with standard security mechanisms",
          },
          {
            id: "PROTECTED",
            name: "High",
            description:
              "System administrators have no direct access to project data. Users can be managed by sys admins",
          },
          {
            id: "HIGH_SECURE",
            name: "Very high without HSM",
            description:
              "Project master key is encrypted with passwords of assigned users. No general sys admin access",
          },
          {
            id: "HSM",
            name: "Very high with HSM",
            description: `Identical to "Very high without HSM", but project master key is stored in a hardware security module (HSM)`,
          },
        ];
      }

      get projectStatusOfBadge(): BadgeProps["status"] {
        switch (this.props.data.status) {
          case "ENABLED":
            return "success";
          case "DEACTIVATED":
            return "error";
          case "ARCHIVED":
            return "warning";
          default:
            return undefined;
        }
      }

      handleFileUpload = (file) => {
        const { t } = this.props;
        const extension = file.name.substr(file.name.lastIndexOf(".") + 1);
        if (!["xlsx"].includes(extension)) {
          notificationError(t("Invalid file extension"), t("Supported file format .xlsx"));
          return Upload.LIST_IGNORE;
        }
        return true;
      };

      handleFileChange = (info: UploadChangeParam) => {
        const { t, setFolderStructure } = this.props;
        const {
          file,
          file: { name, response, status },
        } = info;
        const loadingStatusCallback = {
          undefined: () => this.setState({ uploading: "ERROR" }),
          uploading: () => this.setState({ uploading: "LOADING" }),
          done: () => {
            notificationSuccess(t("Success"), t(`{{name}} file uploaded successfully`, { name }));
            this.setState({ uploading: "DONE", treeStructure: response }, () => {
              setFolderStructure(this.state.treeStructure);
            });
          },
          error: () => {
            // if file response has message (received from back-end) - use it as tooltip
            if (file.response.message) {
              file.response = file.response.message;
            }
            notificationError(t("Error"), t(`{{name}} file upload failed`, { name }));
            this.setState({ uploading: "ERROR" });
          },
        };
        this.setState({ fileList: [file] });
        loadingStatusCallback[status]?.();
      };

      handleFileRemove = (file) => {
        this.setState(({ fileList }) => {
          const index = fileList.indexOf(file);
          const newFileList = [].concat(fileList);
          newFileList.splice(index, 1);
          this.setState({ treeStructure: [] }, () => {
            this.props.setFolderStructure(null);
          });
          return {
            fileList: newFileList,
            uploading: "RAW",
          };
        });
      };

      renderStructureTree = (nodesData: API_TreeElement | Array<API_TreeElement>) => {
        if (Array.isArray(nodesData)) {
          return nodesData
            .sort((a, b) => stringSort(a, b, "name"))
            .map((item) => {
              if (item.subfolders) {
                return (
                  <Tree.TreeNode
                    active={false}
                    title={item.name}
                    {...item}
                    id={`${item.id}`}
                    key={item.name + Math.floor(Math.random() * 100)}
                  >
                    {this.renderStructureTree(item.subfolders || [])}
                  </Tree.TreeNode>
                );
              }
              return (
                <Tree.TreeNode
                  active={false}
                  title={item.name}
                  {...item}
                  id={`${item.id}`}
                  key={item.name}
                  isLeaf={!item.subfolders}
                />
              );
            });
        } else if (typeof nodesData === "object") {
          return (
            <Tree.TreeNode active={false} title={<>{nodesData.name}</>} key={`root`}>
              {this.renderStructureTree(nodesData.subfolders)}
            </Tree.TreeNode>
          );
        } else {
          return null;
        }
      };

      renderStatusIcon = (status) => {
        const icons = {
          ERROR: {
            className: "text-error",
            icon: ["far", "exclamation-triangle"],
          },
          DONE: {
            className: "text-success",
            icon: ["far", "check-circle"],
          },
        };
        return icons[status];
      };

      render() {
        const { t, isEditView } = this.props;
        const treeNodes = this.renderStructureTree(this.state.treeStructure);
        const colCount = (columns) => {
          return columns - Number(isEditView);
        };
        const { uploading } = this.state;
        const {
          id,
          name,
          description,
          shortname,
          typeSecretCloud,
          extrasNote,
          type,
          representative,
          projectGroupId,
          status,
        } = this.props.data;
        const uploadProps: UploadProps = {
          action: API_URL.ADMIN_PROJECTS.checkDirectory(),
          headers: API.headersObject(),
          onRemove: this.handleFileRemove,
          beforeUpload: this.handleFileUpload,
          onChange: this.handleFileChange,
          showUploadList: true,
          fileList: this.state.fileList.slice(),
          accept: ".xlsx",
          className: "upload-file",
        };

        return (
          <div className="project-basic-tab">
            <Modal
              title={t("Preview of project structure")}
              maskClosable={false}
              onCancel={() => this.setState({ isModalVisible: false })}
              footer={null}
              open={this.state.isModalVisible}
              width={`100%`}
              style={{ maxWidth: 590 }}
            >
              <Tree showLine={true} defaultExpandedKeys={[`root`]} className="folder-icons">
                {treeNodes}
              </Tree>
            </Modal>
            <Row gutter={20}>
              {isEditView && (
                <Col span={1}>
                  <Form.Item label={"PID"}>{id}</Form.Item>
                </Col>
              )}
              <Col xs={colCount(24)} md={colCount(12)} lg={colCount(8)}>
                <Form.Item
                  label={t("Name")}
                  name="name"
                  initialValue={name}
                  rules={[
                    {
                      required: true,
                      message: t("Please provide a project name"),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8}>
                <Form.Item
                  label={t("Description")}
                  name="description"
                  initialValue={description}
                  rules={[
                    {
                      required: true,
                      message: t("Please provide a description"),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8}>
                <Form.Item label={t("Type")} name="type" initialValue={type || "NONE"}>
                  <Radio.Group>
                    <Radio.Button value={"CLASSIC"}>{t("Classic")}</Radio.Button>
                    <Radio.Button value={"MODERN"}>{t("Modern")}</Radio.Button>
                    <Radio.Button value={"NONE"}>{t("Not set")}</Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8}>
                <Form.Item
                  label={t("Short name")}
                  name="shortname"
                  initialValue={shortname}
                  rules={[
                    {
                      required: true,
                      message: t("Please provide a short name"),
                    },
                  ]}
                >
                  <Input
                    suffix={
                      <Tooltip title={t("Is used as part of the mail sender address")}>
                        <FontAwesomeIcon icon={["far", "question-circle"]} />
                      </Tooltip>
                    }
                  />
                </Form.Item>
              </Col>
              {!isEditView && (
                <Col xs={24} md={12} lg={8}>
                  <Form.Item
                    label={t("Owner")}
                    name="ownerId"
                    initialValue={userInfoStore.id}
                    rules={[
                      {
                        required: true,
                        message: t("Please provide an owner"),
                      },
                    ]}
                  >
                    <Select disabled={true}>
                      <Select.Option key={`${userInfoStore.id}`} value={userInfoStore.id}>
                        {userInfoStore.name}
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} md={12} lg={8}>
                <Form.Item label={t("Representative")} name="representative" initialValue={representative}>
                  <Input />
                </Form.Item>
              </Col>
              {isEditView && (
                <Col xs={24} md={12} lg={8}>
                  <Form.Item
                    className="status-label" // NOTE: this className is designed for use only with label text = "Status"
                    label={
                      <span>
                        {t("Status")}
                        <Badge status={this.projectStatusOfBadge} />
                      </span>
                    }
                    name="status"
                    initialValue={status || "ACTIVE"}
                  >
                    <Radio.Group>
                      <Radio.Button value={"ENABLED"}>{t("Active")}</Radio.Button>
                      <Radio.Button value={"DEACTIVATED"}>{t("Deactivated ")}</Radio.Button>
                      <Radio.Button value={"ARCHIVED"}>{t("Archived")}</Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} md={12} lg={8}>
                <Form.Item
                  label={t("Project group")}
                  name="projectGroupId"
                  initialValue={projectGroupId}
                  normalize={(val) => (val === undefined ? null : val)}
                >
                  <Select allowClear={true} showSearch={true} optionFilterProp="children">
                    {projectGroupList.data.slice().map((prGp) => {
                      return (
                        <Select.Option key={`${prGp.id}`} value={prGp.id}>
                          {prGp.name}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              </Col>
              {!isEditView && (
                <Col xs={24} md={12}>
                  <Form.Item label={t("Project template")} name="preset" initialValue={null}>
                    <Select allowClear={true} showSearch={true} optionFilterProp="children">
                      {this.state.presets.map((item) => (
                        <Select.Option key={item.value} value={item.value}>
                          {item.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} md={12}>
                <Form.Item label={t("Extras")} name="extrasNote" initialValue={extrasNote}>
                  <Input.TextArea />
                </Form.Item>
              </Col>
            </Row>
            {!isEditView && (
              <>
                <Divider>{t("Add pre-defined directory structure")}</Divider>
                <Row justify="center" align="middle" style={{ margin: "30px 0", padding: "20xp" }}>
                  <Col xs={24} sm={6} className="mb-2">
                    <Upload {...uploadProps}>
                      <Button>
                        <FontAwesomeIcon icon={["far", "upload"]} style={{ marginRight: 5 }} />
                        {t("Choose Excel file")}
                      </Button>
                    </Upload>
                  </Col>
                  {uploading === "DONE" && (
                    <Col span={24} className="text-center mb-2">
                      <Button
                        type="default"
                        onClick={() => this.setState({ isModalVisible: true })}
                        style={{ margin: "0 10px" }}
                      >
                        {t("Preview")}
                      </Button>
                      {/* <Button
                  type="primary"
                  onClick={() => this.props.setFolderStructure(this.state.treeStructure)}
                  style={{ margin: '0 10px' }}
                >
                  {t('Save')}
                </Button> */}
                      <Button
                        type="primary"
                        danger
                        onClick={() => {
                          this.setState(
                            { isModalVisible: false, fileList: [], uploading: "RAW", treeStructure: [] },
                            () => {
                              this.props.setFolderStructure(null);
                            }
                          );
                        }}
                      >
                        {t("Abort")}
                      </Button>
                    </Col>
                  )}
                  <Col span={24} className="text-center">
                    *{t("Supported file format .xlsx")}
                  </Col>
                </Row>
              </>
            )}
            {!isEditView && (
              <>
                <Divider>{t("Security levels - Attention, cannot be changed afterwards!")}</Divider>
                <Form.Item name="typeSecretCloud" initialValue={typeSecretCloud || "NONE"}>
                  <Radio.Group buttonStyle="solid">
                    <Row justify="center" gutter={24}>
                      {this.securityLevels.map((level) => {
                        return (
                          <Col xs={24} md={12} lg={6} key={level.id}>
                            <Card
                              className="text-center basic-tab-card"
                              title={<Radio.Button value={level.id}>{t(level.name)}</Radio.Button>}
                              variant="outlined"
                            >
                              {t(level.description)}
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  </Radio.Group>
                </Form.Item>
              </>
            )}
          </div>
        );
      }
    }
  )
);
